/**
 * Secure Storage Utility for WellieMD Questionnaire
 * Uses SubtleCrypto AES-GCM for encryption of draft data in sessionStorage.
 */

const KEY_NAME = 'welliemd_draft_k';
const IV_LENGTH = 12;

/**
 * Derives or retrieves the encryption key for this device.
 * The key is stored in localStorage to survive browser/tab closure.
 */
async function getOrCreateKey() {
  const storedKey = localStorage.getItem(KEY_NAME);
  if (storedKey) {
    try {
      const keyData = new Uint8Array(JSON.parse(storedKey));
      return await window.crypto.subtle.importKey(
        "raw",
        keyData,
        "AES-GCM",
        true,
        ["encrypt", "decrypt"]
      );
    } catch (e) {
      console.warn("Failed to import draft key, recreating...", e);
    }
  }

  const key = await window.crypto.subtle.generateKey(
    { name: "AES-GCM", length: 256 },
    true,
    ["encrypt", "decrypt"]
  );

  const exported = await window.crypto.subtle.exportKey("raw", key);
  localStorage.setItem(KEY_NAME, JSON.stringify(Array.from(new Uint8Array(exported))));
  return key;
}

/**
 * Encrypts data using AES-GCM.
 * @param {Object} data - Plain text data to encrypt
 * @returns {Promise<string>} Base64 encoded string containing IV + Ciphertext
 */
export async function encryptDraft(data) {
  try {
    const key = await getOrCreateKey();
    const iv = window.crypto.getRandomValues(new Uint8Array(IV_LENGTH));
    const encoded = new TextEncoder().encode(JSON.stringify(data));

    const ciphertext = await window.crypto.subtle.encrypt(
      { name: "AES-GCM", iv },
      key,
      encoded
    );

    // Combine IV and Ciphertext for storage
    const combined = new Uint8Array(iv.length + ciphertext.byteLength);
    combined.set(iv);
    combined.set(new Uint8Array(ciphertext), iv.length);

    // Convert Uint8Array to string in chunks to avoid call stack overflow
    // Process in batches of 8192 elements (safe chunk size)
    const CHUNK_SIZE = 8192;
    let result = '';
    for (let i = 0; i < combined.length; i += CHUNK_SIZE) {
      const chunk = combined.slice(i, i + CHUNK_SIZE);
      result += String.fromCharCode.apply(null, Array.from(chunk));
    }
    return btoa(result);
  } catch (e) {
    console.error("Encryption failed:", e);
    return null;
  }
}

/**
 * Decrypts data using AES-GCM.
 * @param {string} encryptedBase64 - Base64 encoded string from encryptDraft
 * @returns {Promise<Object|null>} Decrypted data or null if failed
 */
export async function decryptDraft(encryptedBase64) {
  if (!encryptedBase64) return null;

  try {
    const key = await getOrCreateKey();
    const combined = new Uint8Array(
      atob(encryptedBase64)
        .split("")
        .map((c) => c.charCodeAt(0))
    );

    const iv = combined.slice(0, IV_LENGTH);
    const ciphertext = combined.slice(IV_LENGTH);

    const decrypted = await window.crypto.subtle.decrypt(
      { name: "AES-GCM", iv },
      key,
      ciphertext
    );

    return JSON.parse(new TextDecoder().decode(decrypted));
  } catch (e) {
    console.error("Decryption failed (likely key/session mismatch):", e);
    return null;
  }
}

/**
 * High-level helper to save draft to localStorage with TTL.
 */
export async function saveDraftToStorage(key, draftData) {
  const wrapper = {
    data: draftData,
    expiresAt: Date.now() + 24 * 60 * 60 * 1000 // 24 hours
  };
  const encrypted = await encryptDraft(wrapper);
  if (encrypted) {
    localStorage.setItem(key, encrypted);
  }
}

/**
 * High-level helper to load draft from localStorage with TTL check.
 */
export async function loadDraftFromStorage(key) {
  const encrypted = localStorage.getItem(key);
  if (!encrypted) return null;
  
  const decrypted = await decryptDraft(encrypted);
  if (!decrypted || !decrypted.expiresAt) return null;

  if (Date.now() > decrypted.expiresAt) {
    console.log("🕒 Draft expired, removing...");
    removeDraftFromStorage(key);
    return null;
  }

  return decrypted.data;
}

/**
 * Clears draft from localStorage.
 */
export function removeDraftFromStorage(key) {
  localStorage.removeItem(key);
}
